import React from 'react';
import { Search, Send, Download, Filter, X, CheckSquare } from 'lucide-react';

export default function SearchBar({
  searchQuery,
  setSearchQuery,
  filterType,
  setFilterType,
  filterProvider,
  setFilterProvider,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  recordTypes,
  providers,
  onSendRecordsClick,
  onBulkDownload,
  bulkCount = 0,
  selectedCount = 0,
  allVisibleSelected = false,
  onToggleSelectAllVisible,
  onClearSelection,
  onClearFilters
}) {
  const hasActiveFilters = searchQuery || filterType !== 'all' || filterProvider !== 'all' || dateFrom || dateTo || selectedCount > 0;

  return (
    <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-card border border-slate-200/60">
      <div className="flex flex-col gap-4">
        {/* Search input */}
        <div className="flex-1 relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-brand-500 transition-colors" />
          <input
            type="text"
            placeholder="Search records (try: lab, radiology, Dr. Smith)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-slate-50/80 border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 transition-all"
          />
        </div>

        {/* Filters grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl text-sm transition-all appearance-none"
            >
              <option value="all">All Types</option>
              {recordTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <select
            value={filterProvider}
            onChange={(e) => setFilterProvider(e.target.value)}
            className="w-full px-3 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl text-sm transition-all appearance-none"
          >
            <option value="all">All Providers</option>
            {providers.map(provider => (
              <option key={provider} value={provider}>{provider}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full px-3 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl text-sm transition-all"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full px-3 py-2.5 bg-slate-50/80 border border-slate-200 rounded-xl text-sm transition-all"
          />
        </div>

        {/* Actions row */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2.5 sm:items-center pt-1">
          <button
            onClick={onSendRecordsClick}
            className="w-full sm:w-auto px-5 py-2.5 bg-gradient-to-r from-brand-600 via-violet-600 to-brand-500 text-white rounded-xl text-sm font-semibold hover:shadow-glow-blue transition-all flex items-center justify-center gap-2"
          >
            <Send className="w-4 h-4" />
            Send Records
          </button>

          <button
            onClick={onToggleSelectAllVisible}
            disabled={bulkCount === 0}
            className="w-full sm:w-auto px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            <CheckSquare className="w-4 h-4" />
            {allVisibleSelected ? 'Deselect All' : 'Select All'}
          </button>

          {onBulkDownload && (
            <button
              onClick={onBulkDownload}
              disabled={bulkCount === 0}
              className="w-full sm:w-auto px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Download {selectedCount > 0 ? `(${selectedCount})` : `(${bulkCount})`}
            </button>
          )}

          {hasActiveFilters && (
            <button
              onClick={() => {
                if (onClearFilters) onClearFilters();
                if (onClearSelection) onClearSelection();
              }}
              className="w-full sm:w-auto px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-2"
            >
              <X className="w-3.5 h-3.5" />
              Clear
            </button>
          )}

          {selectedCount > 0 && (
            <span className="text-[11px] font-semibold text-brand-600 bg-brand-50 px-2.5 py-1 rounded-full self-start sm:self-auto">
              {selectedCount} selected
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
